import type { Restaurant } from "../game/restaurants";
import { setupKinds } from "../game/shift";
import { DISHES } from "../game/dishes";
import { DishIcon } from "./DishIcon";
import { Stars } from "./Stars";

/** How a day goes: washing only, or cooking, serving and washing. */
const STEPS = {
  shelf: [
    ["Pick up", "Walk to the bus tub beside the sink and pick up a dirty dish."],
    ["Scrub", "Drop it in the sink and hold Use to scrub until it's clean. It goes in the rack on the drainboard."],
    ["Wash", "Run the rack through the dish machine."],
    ["Put away", "Take the clean rack out and carry it to the shelf by the pass, where the servers grab dishes."],
    ["Close up", "At closing time new orders stop. Finish the ones already in, and you're not done until every dish is washed and back on the shelf."],
  ],
  cafe: [
    ["Espresso", "Hold Use at the grinder to dose a portafilter, stopping in the band, then lock it into a group head. Stop the shot when it's done: short is sour, long is bitter."],
    ["Milk", "Take a pitcher of milk from the fridge and set it on the steam wand. Take it off when it's hot, before it scalds."],
    ["Build", "Pour the shots and the milk into a paper cup on the bar, and pump the ticket's syrup."],
    ["Serve", "Carry the coffee to the window and call out the name on the ticket."],
    ["Wash", "Used shot glasses and pitchers go in the dirty bin. Rinse them in the sink and put them back on the shelf. At closing, every one has to be clean and away."],
  ],
  window: [
    ["Start a basket", "Grab a clean basket from the shelf and set it on the assembly board. There's room for two, so you can build two orders side by side."],
    ["Cook", "Raw patties from the fridge go on the flat-top. Flip each one when its bottom's browned, take it off when both sides are. Frozen fries go in the fryer until golden."],
    ["Build", "Patties and fries into the basket, then the ticket's toppings from the bins along the board. Toppings go on the basket you last worked on, or the one in your hands."],
    ["Pour", "Some orders come with a drink. Grab a cup at the soda machine and hold Use under its nozzle until it's full, but not over. A drink on its own goes straight out; one with food waits on the window ledge."],
    ["Serve", "Carry the finished basket to the pickup window and hand it out."],
    ["Wash", "Customers drop their baskets and cups in the bus tub. Scrub them, run them through the machine, and stack them back on the shelf."],
    ["Close up", "At closing time new orders stop. Finish the ones already in, and you're not done until every basket and cup is washed and back on the shelf."],
  ],
} as const;

export function Briefing({ restaurant, touch, onStart, onBack }: { restaurant: Restaurant; touch: boolean; onStart: () => void; onBack: () => void }) {
  const setup = restaurant.setup!;
  return (
    <Overlay label={`Your day at ${restaurant.name}`}>
      <p className="text-sm font-bold uppercase tracking-wide text-accent">Today</p>
      <h2 className="text-2xl font-black">{restaurant.name}</h2>
      <p className="text-ink-soft">{restaurant.tagline}</p>

      <ol className="mt-4 space-y-2">
        {STEPS[setup.service].map(([title, text], i) => (
          <li key={title} className="flex gap-3">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-bold text-panel">
              {i + 1}
            </span>
            <span className="text-sm">
              <strong>{title}.</strong> {text}
            </span>
          </li>
        ))}
      </ol>

      {setupKinds(setup)
        .filter((k) => DISHES[k].handWash)
        .map((k) => (
          <p key={k} className="mt-4 flex items-center gap-2 rounded-xl bg-sunny-soft p-2 text-sm">
            <DishIcon kind={k} size={32} />
            <span>
              <strong>{DISHES[k].plural[0]!.toUpperCase() + DISHES[k].plural.slice(1)} are hand wash only.</strong> They come back from the line
              right after cooking. Scrub them in the sink, lift them out and carry them straight to the shelf. There are only {setup.stock[k]}.
            </span>
          </p>
        ))}

      <p className="mt-4 text-sm">
        {setup.service === "window"
          ? "Customers wait at the window, and walk off if it takes too long. Burnt food goes in the trash. If the bus tub overflows, baskets break."
          : setup.service === "cafe"
            ? "Customers wait at the window for their name, and walk off if it takes too long. Out of clean shot glasses or pitchers, nothing gets made: keep washing."
            : "If the shelf runs out of a dish an order needs, the table waits, and eventually walks out. If the bus tub overflows, dishes break."}
      </p>

      <div className="mt-3 flex items-center gap-3 rounded-xl bg-tile p-2 text-sm">
        {setupKinds(setup).map((k) => (
          <span key={k} className="flex items-center gap-1">
            <DishIcon kind={k} size={28} />
            {setup.stock[k]} {DISHES[k].plural}
          </span>
        ))}
      </div>

      <h3 className="mt-4 text-sm font-bold uppercase tracking-wide text-ink-soft">Goals</h3>
      <ul className="mt-1 space-y-1">
        {setup.goals.map((g, i) => (
          <li key={g.label} className="flex items-center gap-2 text-sm">
            <Stars count={i + 1} className="w-14 shrink-0 text-base" />
            {g.label}
          </li>
        ))}
      </ul>

      <p className="mt-4 text-xs text-ink-soft">
        {touch ? (
          "Left thumb walks, drag anywhere to look, and the round button does whatever it says. Hold it to keep scrubbing."
        ) : (
          <>
            <Kbd>W</Kbd>
            <Kbd>A</Kbd>
            <Kbd>S</Kbd>
            <Kbd>D</Kbd> walk · mouse looks · <Kbd>Click</Kbd> or <Kbd>E</Kbd> to use (hold to scrub) · <Kbd>Esc</Kbd> pause
          </>
        )}
      </p>

      <div className="mt-5 flex gap-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-xl border-2 border-steel px-4 py-2 font-bold hover:bg-tile focus-visible:outline-4 focus-visible:outline-accent-soft"
        >
          Back
        </button>
        <button
          type="button"
          autoFocus
          onClick={onStart}
          className="flex-1 rounded-xl bg-accent px-4 py-2 font-bold text-panel shadow hover:brightness-110 focus-visible:outline-4 focus-visible:outline-accent-soft"
        >
          Open the doors
        </button>
      </div>
    </Overlay>
  );
}

export function Overlay({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center overflow-y-auto bg-ink/60 p-4">
      <div role="dialog" aria-modal="true" aria-label={label} className="max-h-full w-full max-w-md overflow-y-auto rounded-2xl bg-panel p-5 shadow-xl">
        {children}
      </div>
    </div>
  );
}

export function Kbd({ children }: { children: React.ReactNode }) {
  return <kbd className="rounded border border-steel-dark bg-tile px-1 font-mono text-[0.7rem]">{children}</kbd>;
}
